<template>
    <CompassDialog :visible.sync="showModal" size="small" @closed="$emit('close')">
        <template #header>
            <div class="dialog__icon--close">
                <CompassIconButton icon="close" @click="$emit('close')" />
            </div>
        </template>
        <template #default>
            <div class="dialog__default acenter">
                <img
                    src="/ressources/logged/img/staff/illustration-popin-company-infos.svg"
                    width="220"
                />
                <h2 class="mt-4 typographies-heading-h2">
                    {{ $l10n.__('Complétez les informations sur votre entreprise') }}
                </h2>
                <p class="m-0 gray-60 typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Ainsi, nous vous proposons des améliorations répondant à vos besoins métier.',
                        )
                    }}
                </p>
                <el-form
                    ref="positionTeamForm"
                    v-loading="loading"
                    class="mt-5 aleft"
                    labelPosition="top"
                    :model="form"
                    :rules="rules"
                >
                    <el-form-item
                        :label="$t.__('common.labels.activity_area')"
                        prop="business_segment"
                        :error="errors.get('business_segment')"
                    >
                        <select-input
                            v-model="form.business_segment"
                            mode="edit"
                            :options="activities"
                            :placeholder="$t.__('PopupAprilTeamPosition.select_area')"
                            :appendToBody="true"
                        />
                    </el-form-item>
                    <el-form-item
                        :label="$t.__('common.labels.potential_users_number')"
                        prop="number_of_employees"
                        :error="errors.get('number_of_employees')"
                    >
                        <select-input
                            v-model="form.number_of_employees"
                            mode="edit"
                            :options="nbemplyees"
                            :placeholder="$t.__('PopupAprilTeamPosition.select_users_number')"
                            :appendToBody="true"
                        />
                    </el-form-item>
                    <p class="bold mb-2">{{ $l10n.__('Et à propos de vous') }}</p>
                    <el-form-item
                        :label="$t.__('PopupAprilTeamPosition.what_team_do_you_work_in')"
                        prop="team"
                        :error="errors.get('team')"
                    >
                        <select-input
                            v-model="form.team"
                            mode="edit"
                            :options="teams"
                            :placeholder="$t.__('PopupAprilTeamPosition.select_team')"
                            :appendToBody="true"
                            @input="updateTeam"
                        />
                    </el-form-item>
                    <el-form-item
                        :label="$t.__('PopupAprilTeamPosition.your_job')"
                        prop="position"
                        :error="errors.get('position')"
                    >
                        <select-input
                            v-model="form.position"
                            mode="edit"
                            :options="activePositions"
                            :placeholder="$t.__('PopupAprilTeamPosition.select_your_job')"
                            :appendToBody="true"
                        />
                    </el-form-item>
                    <div class="mt-5 aright">
                        <CompassButton
                            :label="$t.__('PopupAprilTeamPosition.ask_me_later')"
                            type="secondary"
                            @click="$emit('close')"
                        />
                        <CompassButton
                            :label="$t.__('common.actions.continue')"
                            type="primary"
                            :disabled="isDisabledContinue"
                            class="ml-3"
                            @click.prevent="submitForm('positionTeamForm')"
                        />
                    </div>
                </el-form>
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import FormMixins from 'mixins/FormMixins';
import PopinMixins from 'mixins/PopinMixins';
import StaffApi from 'api/staff/staff';
import { createNamespacedHelpers } from 'vuex';

const { mapState } = createNamespacedHelpers('user');

export default {
    components: {
        SelectInput,
    },
    mixins: [FormMixins, PopinMixins],
    data() {
        return {
            showModal: true,
            loading: true,
            form: {
                business_segment: null,
                number_of_employees: null,
                team: null,
                position: null,
            },
            rules: {
                business_segment: [
                    {
                        required: true,
                        message: this.$l10n.__("Veuillez choisir un secteur d'activité"),
                        trigger: 'blur',
                    },
                ],
                number_of_employees: [
                    {
                        required: true,
                        message: this.$l10n.__("Veuillez choisir un nombre d'employés"),
                        trigger: 'blur',
                    },
                ],
                team: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez choisir une équipe'),
                        trigger: 'blur',
                    },
                ],
                position: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez choisir un poste'),
                        trigger: 'blur',
                    },
                ],
            },
            teams: [],
            activities: [],
            nbemplyees: [],
        };
    },
    computed: {
        activeTeam() {
            return this.teams.find((team) => team.value === this.form.team) ?? null;
        },
        activePositions() {
            return null !== this.activeTeam ? Object.values(this.activeTeam.dependencies) : [];
        },

        ...mapState({
            userId: 'id',
        }),
        /**
         * Check if the user can click on the button continue
         *
         * @returns {boolean}
         */
        isDisabledContinue() {
            const isFullyCompletedForm = Object.values(this.form).every((value) => value !== null);
            return !isFullyCompletedForm;
        },
    },
    created() {
        Promise.all([
            StaffApi.getStaffTeam(this.userId),
            StaffApi.getBusinessActivities(),
            StaffApi.getBusinessEmployees(),
        ])
            .then(([staffTeam, activities, employees]) => {
                this.teams = Object.values(staffTeam.fields);

                if (staffTeam.contact) {
                    this.form.team =
                        staffTeam.contact.team != null
                            ? staffTeam.contact.team
                            : this.teams[0].value;
                    this.form.position =
                        staffTeam.contact.position != null ? staffTeam.contact.position : null;
                }
                this.activities = Object.values(activities);
                this.nbemplyees = Object.values(employees);
                this.loading = false;
            })
            .catch((error) => {
                this.$emit('close');
                Push.error(
                    this.$l10n.__(
                        'Erreur lors de la récupération des informations du collaborateur.',
                    ),
                );
            });
    },
    methods: {
        updateTeam(value) {
            this.form.position = null;
        },
        save() {
            StaffApi.updateSystemfields(this.userId, this.form)
                .then(() => {
                    Push.success(this.$l10n.__('Modifications bien prises en compte.'));
                    this.callNextPopin('corporation-information').then(() => this.$emit('close'));
                })
                .catch((error) => {
                    Push.error(this.$l10n.__("Une erreur est survenue lors de l'enregistrement."));
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.dialog {
    &__icon--close {
        display: flex;
        justify-content: flex-end;
        padding: 24px 32px 0 32px;
    }

    &__default {
        padding-bottom: 24px;
    }
}
</style>
